'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, NumberQuiz, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/calculus'

const DerivativeTracer = dynamic(() => import('../_components/widgets/CalcWidgets').then(m => m.DerivativeTracer), { ssr: false })

const RULES: [string, string, string][] = [
  ['c \\;(\\text{a constant})', '0', 'A flat line has no slope.'],
  ['x', '1', 'The line y = x rises 1 for every 1 across.'],
  ['x^n', 'n\\,x^{n-1}', 'The power rule: bring the power down, subtract one.'],
  ['c \\cdot f(x)', "c \\cdot f'(x)", 'Stretch a curve, stretch its slopes.'],
  ["f(x) + g(x)", "f'(x) + g'(x)", 'Slopes of a sum add up.'],
  ['e^x', 'e^x', 'The function that is its own slope.'],
  ['\\ln x', '\\tfrac{1}{x}', 'Logs flatten out: steep near 0, gentle later.'],
]

export default function DerivativeRules() {
  return (
    <Lesson>
      <Step>
        <H>Draw the slope of every point</H>
        <P>
          The derivative isn’t just one number — at every <M>x</M> the curve has a slope, so the slopes themselves form a new
          function, <M>{"f'(x)"}</M>. Trace it yourself: drag the point along the top curve, and each slope you visit is plotted
          underneath.
        </P>
        <Figure n="4.3" caption="Top: a function and its tangent. Bottom: the slope at each point you’ve visited. Try x² — its slopes trace out a straight line, 2x.">
          <DerivativeTracer />
        </Figure>
        <Callout kind="try">
          With <M>{'x^2'}</M>: where is the slope zero? That’s the bottom of the bowl. With <M>{'e^x'}</M>: notice the bottom
          graph is identical to the top one.
        </Callout>
      </Step>

      <Step gate="q-power">
        <H>Shortcuts</H>
        <P>
          You don’t need to zoom in every time. People worked out the derivatives of all the common functions once, and now we
          just look them up. These handful cover almost everything in this course:
        </P>
        <div className="gd-card my-6 overflow-x-auto p-1">
          <table className="gd-table">
            <thead>
              <tr>
                <th>function</th>
                <th>its derivative</th>
                <th>why it makes sense</th>
              </tr>
            </thead>
            <tbody>
              {RULES.map(([f, d, why]) => (
                <tr key={f}>
                  <td className="text-[17px]">
                    <M>{f}</M>
                  </td>
                  <td className="text-[17px]">
                    <M>{`\\pink{${d}}`}</M>
                  </td>
                  <td className="text-[14.5px]" style={{ color: 'var(--gd-ink-2)' }}>
                    {why}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Exercise id="check-rules" title="Check the rules against reality" {...EX.checkRules} />
        <Quiz
          id="q-power"
          question={<>Using the power rule, what is the derivative of <M>{'x^5'}</M>?</>}
          options={[{ label: <M>{'5x^4'}</M>, correct: true }, { label: <M>{'x^4'}</M>, why: 'Bring the 5 down in front as well.' }, { label: <M>{'5x^5'}</M>, why: 'The power goes down by one.' }, { label: <M>{'4x^5'}</M>, why: 'Bring the old power (5) down, then reduce the power to 4.' }]}
        />
      </Step>

      <Step gate="nq-rule">
        <H>Combining rules</H>
        <P>
          Because slopes of sums add and constants multiply through, you can take a polynomial apart term by term. For{' '}
          <M>{'f(x) = 3x^2 + 2x + 7'}</M>: the derivative of <M>{'3x^2'}</M> is <M>{'6x'}</M>, of <M>{'2x'}</M> is 2, of 7 is 0.
          So <M>{"f'(x) = 6x + 2"}</M>.
        </P>
        <NumberQuiz id="nq-rule" question={<>For <M>{'f(x) = 3x^2 + 2x + 7'}</M>, what is the slope at <M>{'x = 1'}</M>?</>} answer={8} explain={<>f′(x) = 6x + 2, so f′(1) = 6 + 2 = 8.</>} hint={<>First find f′(x) = 6x + 2, then plug in x = 1.</>} />
      </Step>

      <Step gate="poly-derivative">
        <H>Teach the computer the power rule</H>
        <Exercise
          id="poly-derivative"
          title="Polynomials and their derivatives"
          {...EX.polyDerivative}
          hints={[
            <>
              <C>{'c[n] * x ** n'}</C> is the n-th term; loop over n and add them up.
            </>,
            <>
              For the derivative, start at n = 1 (the constant term disappears) and push <C>n * c[n]</C>.
            </>,
          ]}
        />
        <Callout kind="why">
          This is <strong>symbolic</strong> differentiation: computing the derivative formula exactly, not by wiggling. Deep
          learning libraries do something cleverer still — they apply these rules automatically to every operation in your
          program. You’ll build exactly that in Module 7.
        </Callout>
      </Step>
    </Lesson>
  )
}
